/* ===================== CREATE / EDIT COURSE MODAL =====================
   CREATE MODE (default): a deliberately minimal form — just the three
   required fields (title, course_category_id, difficulty_level) posted to
   POST /courses, plus status:"published" always (drafts are invisible to
   every course list, including this dashboard). Everything else is
   configured later from the course's details page. The course is owned by
   the authenticated instructor server-side, so instructor_ids is not
   collected here.

   EDIT MODE: pass the raw detail-shape course (camelCased read payload) as
   `course` and the FULL form renders instead, saving via PUT /courses/:id
   with ONLY the fields the instructor actually changed (react-hook-form
   dirtyFields) — untouched keys are left out of the payload entirely.
   Fields are prefilled from the read shape; category comes back as a NAME
   string on reads, so the id is matched against the category options by
   label once they load (setValue without shouldDirty, so the prefill itself
   never counts as a change). A course visible to this dashboard is by
   definition published, so status defaults to "published" when editing.

   Same form idiom as CreateLiveClassModal: react-hook-form (@astro/forms),
   the dashboard's Field/input skin, and Laravel 422 field errors mapped
   inline via setError. Every dropdown is a react-select (never a native
   <select> — house rule) in the shared selectStyles skin; the Category
   options are fetched live (useCourseCategories → GET /course-categories),
   the rest are fixed enums. Success invalidates the instructor's course
   queries so the "By Course" section and browse row refresh, then closes. */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Select from 'react-select'
import { api } from '@astro/api'
import { Controller, useForm } from '@astro/forms'
import { DateField } from '@astro/date-field'
import { selectStyles } from '@astro/astrologer-forms'
import { useCourseCategories } from '../useDashboard'

const inputCls =
  'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors'

const DIFFICULTY_OPTIONS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
]
const COURSE_TYPE_OPTIONS = [
  { value: 'recorded', label: 'Recorded (self-paced)' },
  { value: 'live', label: 'Live (batch)' },
]
const CURRENCY_OPTIONS = [
  { value: 'INR', label: '₹ INR' },
  { value: 'USD', label: '$ USD' },
]
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
]

function Field({ label, error, className = '', children }) {
  return (
    <div className={className}>
      <label className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">{label}</label>
      {children}
      {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
    </div>
  )
}

/** A react-select wired to a react-hook-form Controller, used for every
 * dropdown here. `options` may still be loading (Category); the current value
 * is matched by loose string compare so numeric ids round-trip cleanly. */
function SelectField({ control, name, options, placeholder, isLoading, rules, error, inputId }) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <Select
          inputId={inputId}
          options={options}
          value={options.find((o) => String(o.value) === String(field.value)) ?? null}
          onChange={(option) => field.onChange(option?.value ?? '')}
          onBlur={field.onBlur}
          placeholder={placeholder}
          styles={selectStyles}
          isLoading={isLoading}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          menuPosition="fixed"
          className={error ? 'rounded-xl ring-1 ring-red-300' : undefined}
        />
      )}
    />
  )
}

/** A pill toggle for the course's boolean flags — reads better than a bare
 * checkbox row and stays in the gold skin. */
function ToggleField({ label, hint, register, name }) {
  return (
    <label className="flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 cursor-pointer">
      <input type="checkbox" className="w-4 h-4 accent-amber-600 shrink-0" {...register(name)} />
      <span className="min-w-0">
        <span className="block text-sm text-astroDark font-semibold leading-tight">{label}</span>
        {hint && <span className="block text-[10px] text-stone-400 leading-tight mt-0.5">{hint}</span>}
      </span>
    </label>
  )
}

/** Read-payload HTML blurb → plain text the summary textarea can edit. */
function stripHtml(html) {
  return String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

export default function CreateCourseModal({ onClose, course = null }) {
  const queryClient = useQueryClient()
  const [banner, setBanner] = useState(null)
  const isEdit = Boolean(course?.id)

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    getValues,
    control,
    watch,
    formState: { errors, dirtyFields },
  } = useForm({
    defaultValues: isEdit
      ? {
          title: course.title || '',
          course_category_id: '', // matched by name once the category options load
          difficulty_level: course.level || 'beginner',
          course_type: course.courseType || 'recorded',
          language: course.language || '',
          summary: stripHtml(course.shortDescription),
          price: course.price ?? '',
          discount_price: course.discountPrice ?? '',
          accept_part_payment: Boolean(course.acceptPartPayment),
          min_part_price: course.minPartPrice ?? '',
          currency: course.currency || 'INR',
          max_students: course.maxStudents ?? '',
          enrollment_expiration: course.enrollmentExpiration ?? '',
          total_duration: course.totalDuration != null ? String(course.totalDuration) : '',
          status: 'published', // it's readable → it's published
          is_public: String(course.visibility || 'public') === 'public',
          enable_qa: course.enableQa != null ? Boolean(course.enableQa) : true,
          certificate_available: Boolean(course.certificateAvailable),
          lifetime_access: Boolean(course.lifetimeAccess),
        }
      : {
          title: '',
          course_category_id: '',
          difficulty_level: 'beginner',
          course_type: 'recorded',
          language: 'English',
          summary: '',
          price: '',
          discount_price: '',
          accept_part_payment: false,
          min_part_price: '',
          currency: 'INR',
          max_students: '',
          enrollment_expiration: '',
          total_duration: '',
          status: 'published',
          is_public: true,
          enable_qa: true,
          certificate_available: false,
          lifetime_access: false,
        },
    mode: 'onTouched',
  })

  // Lock the page scroll and close on Escape while the dialog is up.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  const { categories, isLoading: categoriesLoading } = useCourseCategories()

  // Edit mode: the read payload carries the category NAME only — resolve it
  // to an id by label once the options land (skipped if the user picked one).
  useEffect(() => {
    if (!isEdit || !categories.length || getValues('course_category_id')) return
    const name = String(course.category || '').trim().toLowerCase()
    const match = categories.find((o) => String(o.label).trim().toLowerCase() === name)
    if (match) setValue('course_category_id', match.value)
  }, [isEdit, categories, course, setValue, getValues])

  const createMutation = useMutation({
    mutationFn: (payload) => (isEdit ? api.courses.update(course.id, payload) : api.courses.create(payload)),
    onSuccess: () => {
      // Refresh every place the instructor's courses surface.
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'instructor-courses'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'courses'] })
      if (isEdit) queryClient.invalidateQueries({ queryKey: ['dashboard', 'course-detail', String(course.id)] })
      onClose()
    },
    onError: (err) => {
      // Laravel 422s carry per-field messages under `details`; field names
      // match the payload keys 1:1, so they surface inline as-is.
      const details = err?.details
      if (details && typeof details === 'object') {
        for (const [key, msgs] of Object.entries(details)) {
          setError(key, { type: 'server', message: Array.isArray(msgs) ? msgs[0] : String(msgs) })
        }
      }
      setBanner(err?.message || `Could not ${isEdit ? 'save' : 'create'} the course. Please try again.`)
    },
  })

  /** Form value → PUT payload value per field. `undefined` = skip the key
   * (blank numeric/date inputs can't express "unset" through this form). */
  const EDIT_FIELD_VALUES = {
    title: (v) => v.title.trim(),
    course_category_id: (v) => Number(v.course_category_id),
    difficulty_level: (v) => v.difficulty_level,
    course_type: (v) => v.course_type,
    language: (v) => v.language.trim(),
    summary: (v) => v.summary.trim(),
    price: (v) => (v.price === '' || v.price == null ? undefined : Number(v.price)),
    discount_price: (v) => (v.discount_price === '' || v.discount_price == null ? undefined : Number(v.discount_price)),
    accept_part_payment: (v) => Boolean(v.accept_part_payment),
    min_part_price: (v) =>
      v.min_part_price === '' || v.min_part_price == null ? null : Number(v.min_part_price),
    currency: (v) => v.currency,
    status: (v) => v.status,
    max_students: (v) => (v.max_students === '' || v.max_students == null ? undefined : Number(v.max_students)),
    enrollment_expiration: (v) => v.enrollment_expiration || undefined,
    total_duration: (v) => v.total_duration.trim() || undefined,
    is_public: (v) => Boolean(v.is_public),
    enable_qa: (v) => Boolean(v.enable_qa),
    certificate_available: (v) => Boolean(v.certificate_available),
    lifetime_access: (v) => Boolean(v.lifetime_access),
  }

  const onSubmit = handleSubmit((v) => {
    setBanner(null)

    // EDIT: PUT only the keys the instructor actually changed — untouched
    // fields stay out of the payload so the backend leaves them as-is.
    if (isEdit) {
      const payload = {}
      for (const [key, toValue] of Object.entries(EDIT_FIELD_VALUES)) {
        if (!dirtyFields[key]) continue
        const value = toValue(v)
        if (value !== undefined) payload[key] = value
      }
      // The API validates the pair together, so they travel together: turning
      // part payment on without its minimum is a guaranteed 422, and turning it
      // off with a stale minimum left behind is just noise in the row.
      if (dirtyFields.accept_part_payment && !dirtyFields.min_part_price) {
        payload.min_part_price = EDIT_FIELD_VALUES.min_part_price(v)
      }
      if (Object.keys(payload).length === 0) {
        onClose() // nothing changed — nothing to save
        return
      }
      createMutation.mutate(payload)
      return
    }

    // CREATE: the required trio, always published — the read API only
    // returns published courses, so a draft would be invisible everywhere
    // (including this dashboard) right after creating it.
    createMutation.mutate({
      title: v.title.trim(),
      course_category_id: Number(v.course_category_id),
      difficulty_level: v.difficulty_level,
      status: 'published',
    })
  })

  const cls = (name) => `${inputCls} ${errors[name] ? 'border-red-300' : 'border-stone-200'}`

  const modal = (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      {/* Click-away backdrop in the site's maroon, matching the hero glow. */}
      <div className="absolute inset-0 bg-[#3D0A0A]/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Edit Course' : 'Create Course'}
        className={`relative bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto p-6 ${isEdit ? 'max-w-2xl' : 'max-w-lg'}`}
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Vedic Academy</span>
            <h3 className="text-xl text-astroDark font-royal">{isEdit ? 'Edit Course' : 'Create Course'}</h3>
            <p className="text-xs text-stone-500 mt-0.5">
              {isEdit
                ? <>Update the details of &ldquo;{course.title}&rdquo;.</>
                : 'Just the essentials — manage everything else from the course’s details page.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 shrink-0 rounded-full border border-stone-200 text-stone-500 hover:bg-stone-50 flex items-center justify-center text-sm font-bold"
          >
            ✕
          </button>
        </div>

        {banner && (
          <div className="mb-4 px-4 py-3 rounded-xl text-xs font-semibold bg-red-50 text-red-600 border border-red-100">
            {banner}
          </div>
        )}

        <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Course Title" error={errors.title?.message} className="sm:col-span-2">
            <input
              className={cls('title')}
              placeholder="e.g. Vedic Astrology Fundamentals"
              {...register('title', { required: 'Course title is required' })}
            />
          </Field>

          <Field label="Category" error={errors.course_category_id?.message}>
            <SelectField
              control={control}
              name="course_category_id"
              inputId="course-category"
              options={categories}
              placeholder={categoriesLoading ? 'Loading…' : 'Select a category'}
              isLoading={categoriesLoading}
              rules={{ required: 'Category is required' }}
              error={errors.course_category_id}
            />
          </Field>

          <Field label="Difficulty" error={errors.difficulty_level?.message}>
            <SelectField
              control={control}
              name="difficulty_level"
              inputId="course-difficulty"
              options={DIFFICULTY_OPTIONS}
              placeholder="Select difficulty"
              rules={{ required: 'Difficulty is required' }}
              error={errors.difficulty_level}
            />
          </Field>

          {isEdit && (<>
          <Field label="Course Type" error={errors.course_type?.message}>
            <SelectField
              control={control}
              name="course_type"
              inputId="course-type"
              options={COURSE_TYPE_OPTIONS}
              placeholder="Select type"
            />
          </Field>

          <Field label="Language" error={errors.language?.message}>
            <input className={cls('language')} placeholder="e.g. English" {...register('language')} />
          </Field>

          <Field label="Summary" error={errors.summary?.message} className="sm:col-span-2">
            <textarea
              rows={3}
              className={`${cls('summary')} resize-none`}
              placeholder="A short introduction to what this course covers."
              {...register('summary')}
            />
          </Field>

          <Field label="Price (₹)" error={errors.price?.message}>
            <input type="number" min="0" step="0.01" className={cls('price')} placeholder="1999" {...register('price')} />
          </Field>

          <Field label="Discount Price (₹)" error={errors.discount_price?.message}>
            <input type="number" min="0" step="0.01" className={cls('discount_price')} placeholder="1499" {...register('discount_price')} />
          </Field>

          {/* Part payment. The minimum only matters when the toggle is on, so
              it is revealed rather than always shown — and it is the floor on
              the FIRST payment, which the installment plan's opening amount
              then has to clear. The plan itself lives on the course's details
              page, since it is a list rather than a field. */}
          <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ToggleField
              register={register}
              name="accept_part_payment"
              label="Accept part payment"
              hint="Students can pay in stages"
            />
            {watch('accept_part_payment') && (
              <Field label="Minimum First Payment (₹)" error={errors.min_part_price?.message}>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={cls('min_part_price')}
                  placeholder="e.g. 500"
                  {...register('min_part_price', {
                    validate: (value, values) => {
                      if (!values.accept_part_payment) return true
                      const min = Number(value)
                      if (value === '' || value == null || Number.isNaN(min) || min <= 0)
                        return 'Set the smallest first payment you will accept.'
                      const price = Number(values.price)
                      if (price > 0 && min >= price) return 'This must be less than the course price.'
                      return true
                    },
                  })}
                />
              </Field>
            )}
          </div>

          <Field label="Currency" error={errors.currency?.message}>
            <SelectField
              control={control}
              name="currency"
              inputId="course-currency"
              options={CURRENCY_OPTIONS}
              placeholder="Currency"
            />
          </Field>

          <Field label="Status" error={errors.status?.message}>
            <SelectField
              control={control}
              name="status"
              inputId="course-status"
              options={STATUS_OPTIONS}
              placeholder="Status"
            />
            {/* The read API returns published courses only — a draft is real
                but invisible everywhere (including this dashboard) until
                published, which reads as a silent failure without a warning. */}
            {watch('status') === 'draft' && (
              <p className="text-[10px] text-amber-600 mt-1">
                {isEdit
                  ? 'Switching to draft hides this course everywhere (including this dashboard) until republished.'
                  : 'Drafts are saved but won’t appear in any course list (including yours) until published.'}
              </p>
            )}
          </Field>

          <Field label="Max Students" error={errors.max_students?.message}>
            <input type="number" min="0" step="1" className={cls('max_students')} placeholder="No limit" {...register('max_students')} />
          </Field>

          <Field label="Enrollment Closes" error={errors.enrollment_expiration?.message}>
            <Controller
              name="enrollment_expiration"
              control={control}
              render={({ field }) => (
                <DateField
                  className={cls('enrollment_expiration')}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          </Field>

          <Field label="Total Duration" error={errors.total_duration?.message} className="sm:col-span-2">
            <input className={cls('total_duration')} placeholder="e.g. 10h 30m" {...register('total_duration')} />
          </Field>

          <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ToggleField register={register} name="is_public" label="Publicly listed" hint="Show in the course catalog" />
            <ToggleField register={register} name="enable_qa" label="Enable Q&A" hint="Students can ask questions" />
            <ToggleField register={register} name="certificate_available" label="Certificate" hint="Issued on completion" />
            <ToggleField register={register} name="lifetime_access" label="Lifetime access" hint="No enrollment expiry" />
          </div>
          </>)}

          <div className="sm:col-span-2 flex items-center justify-end gap-2 pt-4 border-t border-stone-100">
            <button
              type="button"
              onClick={onClose}
              className="border border-stone-200 px-5 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save Changes' : 'Create Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  // Portal to <body> so the overlay escapes the dashboard's stacking context
  // and paints above the site's fixed header (z-50); z-[1200] clears the suite's fixed mobile bottom nav (z-100) and stays under the
  // react-select menu portal (z-1250) so dropdowns still open above the dialog.
  return typeof document === 'undefined' ? null : createPortal(modal, document.body)
}
